import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import Flexbox from 'flexbox-react';

import LoadingSpinner from 'components/core/LoadingSpinner';
import InfoBoxPageHeader from 'components/core/InfoBoxPageHeader';
import DataExplorerService from 'services/DataExplorerService';
import { colorBlue, colorBorderGrey, colorRed, colorLabelBlue, colorTextGrey, getAnchorStyle, getButtonStyle } from 'styles/base';
import AbstractLocalStorageComponent from 'components/core/AbstractLocalStorageComponent';
import ProcessUtils from 'utils/ProcessUtils';
import ModalsService from 'services/ModalsService';
import DataExplorerParametersForm from 'components/core/data_explorer/DataExplorerParametersForm';
import DropdownBox from 'components/core/DropdownBox';
import InfoBoxLabel from 'components/core/InfoBoxLabel';

const explorerWrapperStyle = css`
    max-width: 35%;
    min-width: 250px;
    background-color: white;
    margin-right: -15px;
    margin-left: 15px;
    height: 100%;
    border: 1px solid #ccc;
    border-radius: 2px;
    padding: 5px 10px;
    box-shadow: 0 10px 16px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    box-sizing: border-box;
`;

const noShrink = css`
flex: 1 0 auto;
`;

const explorerTitle = css`
    font-weight: 900;
    margin-bottom: 5px;
`;

const childrenWrapperStyle = css`
    padding: 10px 0;
`;

const headingStyle = css`
    max-width: 700px;
`;

const textStyle = css`
    color: ${colorTextGrey};
    font-style: italic;
    line-height: 14px;
`;

const explorerNotesStyle = css`
    ${textStyle}
    margin-bottom: 10px;
    font-size: 12px;
`;

const buttonStyle = getButtonStyle('white', colorBlue);

const closeButtonStyle = getAnchorStyle('initial', 'initial');

const redStyle = css`
    color: ${colorRed};
`;

export default class DataExplorerPage extends AbstractLocalStorageComponent {
    static propTypes = Object.assign({
        children: PropTypes.any,
        process: PropTypes.object, // the process from the store
        exploredDatastreams: PropTypes.object,
        siteFacilites: PropTypes.array,
        className: PropTypes.string
    }, AbstractLocalStorageComponent.propTypes);

    constructor(props) {
        super(props);

        this.state = {
            visible: false
        };
    }

    /**
     * Multiple pages could have the data explorer shown on them, but there is a shared state
     * for the data explorer across all pages of the same process (so it can stay open between page navigation
     * and have the same datastream displayed).
     * @param {string} processName 
     */
    static resetStateForProcess(processName) {
        const key = DataExplorerPage.getDataExplorerId(processName);
        AbstractLocalStorageComponent.clearState(key);
    }

    static getDataExplorerId(processName) { 
        // Unique ID for sharing data explorer state on all relevant pages for a given process
        return `${processName}_data_explorer`;
    }

    getStorageId() {
        return DataExplorerPage.getDataExplorerId(this.props.process.name);
    }

    isVisible() {
        return !!this.state.visible;
    }

    getActiveInputDatastream() {
        let activeDatastream = this.getLSState().activeDatastream || null;

        if (!activeDatastream && this.props.process.input_datastreams.length > 0) {
            // If no active datastream selected, then choose the first one
            activeDatastream = this.props.process.input_datastreams[0];
        
        } else if (!_.includes(this.props.process.input_datastreams, activeDatastream)) {
            // If the datastream is no longer in the process (i.e., it was deleted) then
            // choose the first one
            activeDatastream = this.props.process.input_datastreams[0];
        }

        return activeDatastream;
    }

    /**
     * Get all dimensions for the active datastream
     */
    getDimensions() {
        const activeDatastream = this.getActiveInputDatastream();
        const exploredDatastream = this.props.exploredDatastreams[activeDatastream].currentData;
        return Object.keys(exploredDatastream.dimensions);
    }

    /**
     * Get all variables for the active datastream
     */
    getVariables(datastreamName) {
        if (!datastreamName) {
            datastreamName = this.getActiveInputDatastream();
        }
        // Get the retrieved variables
        const retrievedVariables = ProcessUtils.getRetrievedVariablesForDatastream(this.props.process, datastreamName);

        // Now find the original variable name from the datastream
        const datastreamVariables = [];
        retrievedVariables.forEach(retrievedVariableName => {
            const datastreamVariableNames = ProcessUtils.getOriginalVariableNameFromInputstream(this.props.process, retrievedVariableName, datastreamName);
            if (datastreamVariableNames.length > 0) {
                datastreamVariables.push(datastreamVariableNames[0]);
            }
        });
        return datastreamVariables;
    }

    getActiveDimension() {
        const activeDatastream = this.getActiveInputDatastream();
        const key = this.getDimensionKey(activeDatastream);
        let activeDimension = this.getLSState()[key];
    
        if (!activeDimension) {
            const exploredDatastream = this.props.exploredDatastreams[activeDatastream].currentData;

            // dimensions might not exist if no files were found for this datastream
            if (exploredDatastream.dimensions) {
                if (Object.keys(exploredDatastream.dimensions).length > 0) {
                    activeDimension = Object.keys(exploredDatastream.dimensions)[0];
                }
            }
        }
        return activeDimension;
    }

    getDimensionKey(activeDatastream) {
        return `${activeDatastream}_dimension`;
    }

    getActiveVariable() {
        const activeDatastream = this.getActiveInputDatastream();
        const key = this.getVariableKey(activeDatastream);
        let activeVariable = this.getLSState()[key];
        const allVariables = this.getVariables(activeDatastream);
    
        if (!activeVariable) {
            // If not set, pick the first one.
            activeVariable = allVariables[0];

        } else if (!_.includes(allVariables, activeVariable)) {
            // If the variable is no longer in the process (i.e., it was deleted) then
            // choose the first one
            activeVariable = allVariables[0];
        }
        return activeVariable;
    }

    getVariableKey(activeDatastream) {
        return `${activeDatastream}_variable`;
    }

    onDatastreamSelected = (menuItem) => {
        this.runDataExplorer(); // make sure the datastream is loaded
        this.setLSState({
            activeDatastream: menuItem.key
        });
    };

    onDimensionSelected = (selectedDimension) => {
        const activeDatastream = this.getActiveInputDatastream();
        const key = this.getDimensionKey(activeDatastream);
        this.setLSState({
            [key]: selectedDimension.key
        });
    };

    onVariableSelected = (selectedVariable) => {
        const activeDatastream = this.getActiveInputDatastream();
        const key = this.getVariableKey(activeDatastream);
        this.setLSState({
            [key]: selectedVariable.key
        });
    };

    onOpenDod = () => {
        ModalsService.openModal({
            id: 'open_dod',
            modalType: ModalsService.TYPE_INFO,
            modalTitle: 'Open DOD',
            modalMessage: 'Capability coming soon...'
        });
    };

    onRerunExplorer = () => {
        const activeDatastream = this.getActiveInputDatastream();
        this.runDataExplorer(null, activeDatastream);
    };

    showExplorer = () => {
        this.setState({
            visible: true
        });
    };

    hideExplorer = () => {
        this.setState({
            visible: false
        });
    };

    /**
     * If the value is a number, then round to 4 significant digits.
     */
    roundValue(value) {
        let formattedValue = value;
        if (!isNaN(value)) {
            const number = Number(value);
            formattedValue = Number(Number(number).toPrecision(4)).toString();
        }
        return formattedValue;
    }

    runDataExplorer = (event, rerunDatastream) => {
        // Check if the explored datastream info is available for each input datastream.  If not, then
        // we call the explorer web service
        const datastreamsToExplore = DataExplorerService.getDatastreamsThatNeedExploring(this.props.process);

        if (rerunDatastream) {
            datastreamsToExplore[rerunDatastream] =
                ProcessUtils.getOriginalVariableNamesFromInputstream(this.props.process, rerunDatastream);
        }

        if (!_.isEmpty(datastreamsToExplore)) {

            const callDataExplorerWebService = () => {
                ProcessUtils.callDataExplorerWebService(this.props.process, datastreamsToExplore);

                // Make sure explorer is open
                this.showExplorer();
            };
        
            // Check if the process has the search folders and run locations defined
            if (rerunDatastream || !ProcessUtils.hasDataExplorerParameters(this.props.process)) {
                const onOk = () => { // dialog ok handler
                    if (ProcessUtils.hasDataExplorerParameters(this.props.process)) {
                        callDataExplorerWebService();
                        return true;
                    } 
                    return false; // can't close the dialog if parameters are missing
                };
                let title = 'Please provide the following information in order to locate the appropriate input files to explore.';
                if (rerunDatastream) {
                    //const exploredDatastream = this.props.exploredDatastreams[rerunDatastream];
                    title = `Click the OK button to re-run the explorer on input datastream ${rerunDatastream} using a different input file.`;
                }
                const modalTitle = (
                    <Flexbox flexDirection="column">
                        <h4>Explore Input Data</h4>
                        <InfoBoxPageHeader message={title} />
                    </Flexbox>
                );
                const form = (
                    <DataExplorerParametersForm
                        processId={this.props.process.name} 
                        siteFacilities = {this.props.siteFacilities}
                    />
                );  

                ModalsService.openModal({
                    id: 'data_explorer_parameters',
                    modalType: ModalsService.TYPE_CONFIRM,
                    modalTitle,
                    modalMessage: form,
                    modalSize: 'large',
                    onOk
                });
                
            } else {
                callDataExplorerWebService();
            }

        } else {
            this.showExplorer();
        }
    };

    isMissingNetcdfData(exploredDatastream) {
        let missing = false;

        if (_.isEmpty(exploredDatastream.currentData.dimensions) &&
            !_.isEmpty(exploredDatastream.currentData.errors)) {

            exploredDatastream.currentData.errors.forEach(error => {
                // TODO: explorer should be using error codes instead
                if (error.startsWith('Folder not found') || error === 'No data files found for datastream') {
                    missing = true;
                }
            });
        }
        return missing;
    }

    renderDatastreamHeader(exploredDatastream) {
        const missingData = this.isMissingNetcdfData(exploredDatastream);
        const divStyle = css`
            margin-bottom: 10px;
        `;
        const pathStyle = css`
            font-weight: bold;
            margin-left: 10px;
        `;
        const errorStyle = css`
            font-weight: bold;
            padding-right: 5px;
        `;
        let rerunStyle = getAnchorStyle(colorLabelBlue, 'white');
        let message;

        if (missingData) {
            message = (
                <div>
                    <div className={divStyle}>No netcdf files were found for this datastream in the specified data locations.</div>
                    <div className={divStyle}>To change the data locations and try a different file, click the "Re-run explorer..." link.</div>
                </div>
            );
            rerunStyle = css`
                line-height: 14px;
                 ${rerunStyle}
            `;

        } else {

            // TODO: we need to check if the exploredFile has sgp/sgpmetE13.b1/ in the path and run location is sgp c1
            message = (
                <div>
                    <div className={divStyle}>Values were obtained from data file:</div>
                    <div className={cx(divStyle, pathStyle)}>{exploredDatastream.currentData.exploredFile}</div>
                    <div className={divStyle}>To find a different file, click the "Re-run explorer..." link.</div>
                </div>
            );
        }

        return (
            <Flexbox  className={css`margin-bottom: 10px;`} flexDirection="row" alignItems="center" flex="0 0 auto">
                <InfoBoxLabel message={message}/>
                <a className={rerunStyle} onClick={this.onRerunExplorer}>
                    {missingData &&
                    <span className={errorStyle}>No data found - </span>
                    }
                    <span>Re-run explorer with a different input file</span>
                </a>
            </Flexbox>
        );
    }

    renderDimensions(exploredDatastream) {
        const marginStyle = css`margin-bottom: 10px;`;
        const dimensionName = this.getActiveDimension();
        const missingData = this.isMissingNetcdfData(exploredDatastream);

        if (missingData) {
            return (
                <DropdownBox className={cx(marginStyle, noShrink)} label="Dimensions:">
                    <a title="No netcdf files were found for this datastream in the specified data locations.">Error</a>
                </DropdownBox>
            );

        } else if (dimensionName) {
            const dimension = exploredDatastream.currentData.dimensions[dimensionName];
            const items = this.getDimensions();
            const section1Data = [
                ['Start:', this.roundValue(dimension.start_point)],
                ['End:', this.roundValue(dimension.end_point)]
            ];
            const section2Data = [
                ['Median:', this.roundValue(dimension.median_interval)],
                ['Average:', this.roundValue(dimension.average_interval)],
                ['Max:', this.roundValue(dimension.max_interval)]
            ];

            return (
                <DropdownBox className={cx(marginStyle, noShrink)}
                    label="Dimensions:" items={items} selectedItem={dimensionName}
                    onSelectionChanged={this.onDimensionSelected}
                >
                    {this.renderTwoColumns('Units:', dimension.units)}
                    <Flexbox flexDirection="row" flex="1 0 auto">
                        {this.renderSection('Values', section1Data)}
                        {this.renderSection('Interval Size', section2Data)}
                    </Flexbox>
                </DropdownBox>
            );
        }
        return (
            <div/>
        );

    }

    renderVariables(exploredDatastream) {
        const variableName = this.getActiveVariable();
        const variable = exploredDatastream.currentData.variables[variableName];
        const items = this.getVariables();
        const missingData = this.isMissingNetcdfData(exploredDatastream);
        let variableUnits;
        let section1Data;

        if (missingData) {
            const missingFileAnchor = (
                <a title="No netcdf files were found for this datastream in the specified data locations.">Error</a>
            );
            variableUnits = missingFileAnchor;
            section1Data = [
                ['Min:', missingFileAnchor],
                ['Max:', missingFileAnchor],
                ['Median:', missingFileAnchor],
                ['Average:', missingFileAnchor]
            ];
        } else {
            variableUnits = variable.units;
            section1Data = [
                ['Min:', this.roundValue(variable.values.min)],
                ['Max:', this.roundValue(variable.values.max)],
                ['Median:', this.roundValue(variable.values.median)],
                ['Average:', this.roundValue(variable.values.average)]
            ];
        }

        let qcType = null;
        let section2Data = null;

        if (variable.qc) {
            qcType = variable.qc.type;
            if (qcType) {
                let qcBadLabel = '';
                let qcIndeterminateLabel = '';
                if (qcType === 'bit') {
                    qcBadLabel = 'Bad bit numbers:';
                    qcIndeterminateLabel = 'Indeterminate bit numbers:';

                } else if (qcType === 'integer') {
                    qcBadLabel = 'Bad values:';
                    qcIndeterminateLabel = 'Indeterminate values:';

                }
                section2Data = [
                    ['Type:', qcType],
                    [qcBadLabel, variable.qc.bad_values.join(', ')],
                    [qcIndeterminateLabel, variable.qc.indeterminate_values.join(', ')]
                ];
            } else {
                section2Data = [
                    ['Type:', <a title="This datastream does not follow ADI QC standards.">Error</a>]
                ];

            }
            section2Data.push(['Missing Value:', variable.values.missing_value]);
        }
        let qcComponent = '';
        if (section2Data) {
            qcComponent = (
                <div>
                    {this.renderSection('QC', section2Data)}
                    <div style={{ marginTop: '10px', display: 'flex', fontWeight: 'bold' }}>
                        <div>*</div>
                        <div style={{ marginLeft: '3px' }}>
                            Note that QC data are obtained from the latest DOD version in the database.
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <DropdownBox label="Variables:" items={items} selectedItem={variableName}
                className={noShrink} onSelectionChanged={this.onVariableSelected}>
                {this.renderTwoColumns('Units:', variableUnits)}
                {this.renderSection('Values', section1Data)}
                {qcComponent}

            </DropdownBox>
        );
    }

    renderTwoColumns(label, component) {
        const labelStyle = css`
            font-weight: bold;
            margin-right: 5px;
        `;
     
        return (
            <Flexbox flexDirection="row">
                <div className={labelStyle}>{label}</div>
                <div>{component}</div>
            </Flexbox>
        );
    }

    renderSection(label, data) {
        let maxChars = 0;
        data.forEach(item => {
            if (item[0].length > maxChars) {
                maxChars = item[0].length;
            }
        });
        const itemLabelWidth = `${Math.min(maxChars * 8, 100)}px`;
        const labelStyle = css`
            font-weight: 900;
            margin-right: 15px;
            border-bottom: 1px solid ${colorBorderGrey};
        `;
        const itemLabelStyle = css`
            width: ${itemLabelWidth};
            margin-right: 5px;
        `;
        const itemWrapperStyle = css`
            line-height: 12px;
            margin: 3px 0;
        `;

        const items = data.map(values => {
            return (
                <Flexbox flexDirection="row" className={itemWrapperStyle} key={values[0]}>
                    <div className={itemLabelStyle}>{values[0]}</div>
                    <div>{values[1]}</div>
                </Flexbox>
            );
        });
     
        return (
            <Flexbox flexDirection="column" flex="1 0 auto" marginTop="8px">
                <div className={labelStyle}>{label}</div>
                {items}
            </Flexbox>
        );
    }

    renderInputDatastream(inputDatastreamName) {
        let component = null;
        const exploredDatastream = this.props.exploredDatastreams[inputDatastreamName];

        if (exploredDatastream && exploredDatastream.error) {
            // we had an execution error
            component = (
                <Flexbox flexDirection="column">
                    {this.renderDatastreamHeader(exploredDatastream)}
                    <div className={redStyle}>{exploredDatastream.error}</div>
                </Flexbox>
            );

        } else if (exploredDatastream && exploredDatastream.currentData) {
            component =  (
                <Flexbox flexDirection="column">
                    {this.renderDatastreamHeader(exploredDatastream)}
                    {this.renderDimensions(exploredDatastream)}
                    {this.renderVariables(exploredDatastream)}
                </Flexbox>
            );

        } else {
            // If the explorer data has not been loaded for the input datastream, then we need to show loading spinner
            component =  <LoadingSpinner />;
        }
        return component;
    }

    renderExplorer() {
        const activeDatastream = this.getActiveInputDatastream();
        const headerStyle = css`
            padding: 2px 8px 2px 8px;
            font-weight: bold;
            border: none;
            border-radius: 4px;
            background-color: ${colorBlue};
            color: white !important;
            justify-content: space-between;
            a.ant-dropdown-link {
                color: white !important;
            }
        `;
        const contentStyle = css`
            border: none;
            background-color: white;
            border-top: none;
            flex: 1;
        `;

        const headerComponent = (
            <a onClick={this.onOpenDod} title="Open DOD in PCM DOD application." 
                className={css`font-weight: normal; font-size: 12px; color: white;`}>Open DOD</a>
        );

        return (
            <Flexbox flexDirection="column" className={cx('dataexplorer-sidebar', explorerWrapperStyle)}>
                <Flexbox flexDirection="row" justifyContent="space-between" alignItems="center" flex="0 0 auto">
                    <div className={explorerTitle}>Input Datastream Information</div>
                    <i onClick={this.hideExplorer}
                        className={cx('fa fa-window-close', closeButtonStyle)}
                        aria-hidden="true"/>
                </Flexbox>
                <div className={explorerNotesStyle}>
                    Use this information to help determine output shape and advanced transformation parameters.</div>
              
                <DropdownBox 
                    items={this.props.process.input_datastreams} 
                    selectedItem={activeDatastream} 
                    onSelectionChanged={this.onDatastreamSelected}
                    headerClass={headerStyle}
                    contentClass={contentStyle}
                    headerComponent={headerComponent}
                >
                    {this.renderInputDatastream(activeDatastream)}
                </DropdownBox>
            </Flexbox>
        );
    }

    render() {
        const dataExplorer = this.isVisible() ? this.renderExplorer() : <div/>;

        // If no input datastreams have been defined, then this button is disabled
        const hasInputDatastreams = this.props.process.input_datastreams && this.props.process.input_datastreams.length > 0;
        const style = hasInputDatastreams ? buttonStyle : cx('disabled', buttonStyle);
        const handler = hasInputDatastreams ? this.runDataExplorer : () => {};
        const title = hasInputDatastreams ? 'Click to open Data Explorer' : 'The Data Explorer may only be utilized after input datastreams have been defined.';
        const button = this.isVisible() ? <div/> :
            <div onClick={handler} className={cx('explore-button', style)} title={title}>Explore Input Data</div>;

        return (
            <Flexbox flexDirection="row" flex="1" className={`data-explorer-page ${this.props.className}`}>
                {/* Page contents */}
                <Flexbox flexDirection="column" flex="1">
                    <Flexbox flexDirection="row" justifyContent="space-between" alignItems="flex-start" className={headingStyle}>
                        <div className={cx(textStyle, css`height: 30px`)}>
                            <span className="data-explorer-message">These questions require knowledge of the inputdata files in order to answer correctly.</span>
                        </div>
                        {button}
                    </Flexbox>

                    <Flexbox flex="1" flexDirection="column" className={`data-explorer-children ${childrenWrapperStyle}`}>
                        {this.props.children}
                    </Flexbox>

                </Flexbox>
                {dataExplorer}
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/core/data_explorer/DataExplorerPage.js